/**
 * MesDonneesPanel — Bouton "Mes données" universel (Article 15 + 17 + 20 RGPD).
 *
 * Accessible depuis tout profil (Membre / Cooper / Expert / Admin).
 * Affiche la liste de TOUS les transferts E2E liés à l'utilisateur, propose :
 *  - Export portable JSON / CSV (Article 20)
 *  - Anonymisation universelle (Article 17)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Download, AlertTriangle, FileArchive, Mail,
  FileSpreadsheet, Globe, Code, Loader2, CheckCircle2, XCircle, Calendar,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getToken } from '../services/tokenService';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TYPE_LABELS = {
  zip_share: { label: 'Partage ZIP', icon: FileArchive, color: 'text-indigo-600' },
  email_pdf: { label: 'Email PDF', icon: Mail, color: 'text-blue-600' },
  csv_export: { label: 'Export CSV', icon: FileSpreadsheet, color: 'text-emerald-600' },
  public_link: { label: 'Lien public', icon: Globe, color: 'text-amber-600' },
  api_export: { label: 'Export API', icon: Code, color: 'text-purple-600' },
};

function formatDateFR(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}

export default function MesDonneesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rgpd/me`, { headers: headers() });
      setData(res.data);
    } catch {
      toast.error('Impossible de charger vos transferts RGPD');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const downloadPortability = async (format = 'json') => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/rgpd/me/portability?format=${format}`, {
        headers: headers(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rgpd_mes_donnees.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const requestAnonymize = async () => {
    if (!window.confirm(
      'Êtes-vous sûr de vouloir anonymiser vos données ?\n\n' +
      '• Tous vos transferts seront marqués "anonymisés"\n' +
      '• Vos emails / IPs / User-Agents seront effacés\n' +
      '• L\'audit-trail (dates, types) sera conservé pour conformité Article 30\n\n' +
      'Cette action est IRRÉVERSIBLE.'
    )) return;
    setAnonymizing(true);
    try {
      const res = await axios.post(`${API}/rgpd/me/anonymize`, null, { headers: headers() });
      toast.success(`${res.data.anonymized_transfers} transfert(s) anonymisé(s)`);
      await load();
    } catch {
      toast.error('Erreur anonymisation');
    } finally {
      setAnonymizing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500 inline" />
        <p className="text-sm text-slate-500 mt-2">Chargement de vos transferts…</p>
      </div>
    );
  }

  const transfers = data?.transfers || [];
  const byType = data?.by_type_count || {};

  return (
    <div className="space-y-5" data-testid="mes-donnees-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-amber-50 rounded-xl p-5 border border-indigo-200">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">RGPD — DROITS UTILISATEUR</div>
            <h3 className="text-lg font-bold text-slate-900">Mes données personnelles</h3>
            <p className="text-xs text-slate-600 mt-1">
              Vous avez un droit d'accès (Art. 15), de portabilité (Art. 20) et d'effacement (Art. 17) sur l'ensemble des transferts liés à votre compte.
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(TYPE_LABELS).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = byType[key] || 0;
          return (
            <div key={key} className="bg-white rounded-lg border border-slate-200 p-3" data-testid={`mes-donnees-kpi-${key}`}>
              <Icon className={`h-4 w-4 ${meta.color} mb-1`} />
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{meta.label}</div>
              <div className="text-xl font-bold text-slate-900">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3">Mes droits RGPD</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button
            variant="outline"
            onClick={() => downloadPortability('json')}
            disabled={exporting}
            className="justify-start"
            data-testid="download-portability-json"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2 text-indigo-600" />}
            <div className="text-left">
              <div className="text-xs font-semibold">Article 20 — Portabilité</div>
              <div className="text-[10px] text-slate-500">Export JSON</div>
            </div>
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadPortability('csv')}
            disabled={exporting}
            className="justify-start"
            data-testid="download-portability-csv"
          >
            <Download className="h-4 w-4 mr-2 text-emerald-600" />
            <div className="text-left">
              <div className="text-xs font-semibold">Article 20 — Portabilité</div>
              <div className="text-[10px] text-slate-500">Export CSV</div>
            </div>
          </Button>
          <Button
            variant="outline"
            onClick={requestAnonymize}
            disabled={anonymizing}
            className="justify-start border-red-200 text-red-700 hover:bg-red-50"
            data-testid="request-anonymize"
          >
            {anonymizing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
            <div className="text-left">
              <div className="text-xs font-semibold">Article 17 — Effacement</div>
              <div className="text-[10px] text-red-500">Anonymiser mes données</div>
            </div>
          </Button>
        </div>
      </div>

      {/* Liste des transferts */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            Mes transferts ({transfers.length})
          </div>
        </div>
        {transfers.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Aucun transfert enregistré sur votre compte.</div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {transfers.slice(0, 100).map((t) => {
              const meta = TYPE_LABELS[t.transfer_type] || { label: t.transfer_type, icon: FileArchive, color: 'text-slate-500' };
              const Icon = meta.icon;
              const isActive = t.status === 'active';
              return (
                <div key={t.id} className="p-3 hover:bg-slate-50 flex items-start gap-3" data-testid={`mes-donnees-item-${t.id}`}>
                  <Icon className={`h-4 w-4 ${meta.color} mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {t.payload_summary?.zip_filename || t.purpose || meta.label}
                      </span>
                      {isActive ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-semibold">
                          <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Actif
                        </Badge>
                      ) : t.status === 'revoked' ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] font-semibold">
                          <XCircle className="h-2.5 w-2.5 mr-0.5" />Révoqué
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-700 text-white border-slate-900 text-[10px] font-semibold">
                          <Shield className="h-2.5 w-2.5 mr-0.5" />{t.status === 'anonymized' ? 'Anonymisé' : t.status}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                      <span><Calendar className="inline h-2.5 w-2.5 mr-0.5" />{formatDateFR(t.created_at)}</span>
                      {t.recipient_email && <span>→ {t.recipient_email}</span>}
                      <span className="text-slate-400">Conservé jusqu'au {formatDateFR(t.retention_until)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
